/* eslint-disable no-console */
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'

import SwipeView from '../components/SwipeView'
import Button from '../components/Button'
import ExpandingOptionSelector, { ExpandingOptionSelectorClassName } from '../components/ExpandingOptionSelector'

import OtherPlaceholderImage from '../assets/other_placeholder.png'
import OtherProPlaceholderImage from '../assets/other_pro_placeholder.png'

import './SelectorScreen.scss'
import {
  BaseProductType,
  IconicRange,
  ProductOrientationSupport,
  ProductSeries,
  SkinProductOption,
  TopLevelMenuCategory,
} from '../types/productTypes'

import {
  doesSkinHaveSlots,
  findSkinByItemNo,
  getAdditionalSkinGridOptions,
  getDefaultButtons,
  getSkinSlotCount,
  getTopLevelMenuCategoryForProduct,
} from '../products/productUtils'
import { ProductDataContext } from '../ProductDataContext'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import {
  IconButtons,
  IconMenuSocket,
  IconOrientation,
  IconPlusWhite,
  IconScrollLeft,
  IconStyle,
  IconType,
} from '../components/icons'

import classNames from 'classnames'
import WallColors from '../data/WallColors'
import { SelectorProductPopup } from '../components/SelectorProductPopup'
import { ConfiguredInterface } from '../list/ListTypes'
import { BuildConfig } from '../config/BuildConfig'
import { useOneshotDuration } from '../hooks/useOneshotDuration'
import ValidationBanner from '../components/ValidationBanner'
import windowSupportsTouchEvents from '../utils/windowSupportsTouchEvents'
import { SelectorScreenMenuType } from './SelectorScreenMenuType'
import { UserType } from '../types/userTypes'
import { doesProductPartialMatch, filterByBaseItemNoAndColor } from './selectorScreenUtils'
import { throwErrorInline } from '../utils/throwErrorInline'
import SquareContainer from '../components/SquareContainer'
import { DefaultSwitchSelectionProducts, TopLevelSelectionProducts } from '../TopLevelSelectionProducts'
import { useSmoothScroller } from '../hooks/useSmoothScroller'
import { SelectorProductItem } from '../components/SelectorProductItem'
import { ProductDataShape } from '../ProductDataShape'
import { ToggleSeries } from '../components/ToggleSeries'
import { findOptimalGrid, getAllGridOptions } from '../products/findOptimalGrid'
import { HintPosition } from '../Enums'
import { AppStateActions, WiserOrMomentary } from '../state/AppState'
import HintWrapper from '../components/HintWrapper'
import { ENABLE_ELECTRICIAN_VIEW } from '../environment'
import { WholesalerContext } from '../wholesaler-integration/WholesalerContext'
import {
  getAvailableMenusForTopLevelProductType,
  getDefaultMenuForTopLevelType,
  getProductsAvailableForUserAndSeries,
  getStyleMenuOptionsAvailableForUser,
  getTopLevelCategoryDisplayName,
  OrientationOptions,
  OutdoorTypeOptions,
  TopLevelMenuOptions,
} from './SelectorScreenNavigation'
import { EmptyArray } from '../utils/EmptyArray'
import { formatList } from '../utils/formatList'
import { getProductDisplayNames } from '../utils/getProductDisplayNames'
import { TopLevelSortOrderMap } from '../products/SortOrders'
import { isColorLight, isColorLowContrast } from '../data/colorUtils'
import { WiserSwitchesProductItemNo } from '../data/wiserSwitchesProduct'
import { AnalyticsContext } from '../analytics/AnalyticsContext'
import { getAnalyticsProductInfo, getEcommercePayloadFrom } from '../analytics/AnalyticsHelpers'
import lodash from 'lodash'
import { CookieSettingsLink } from '../components/CookieSettingsLink'
import { PrivacyPolicyLink } from '../components/PrivacyPolicyLink'

interface Props {
  isEntryFromDeepLink: boolean
  selectedProduct: SkinProductOption
  allProducts: readonly SkinProductOption[]
  setSelectedProduct: (product: SkinProductOption) => void
  configuredButtonIds: readonly ConfiguredInterface[]
  onClickCustomise: (() => void) | null
  onClickSave: () => void
  isInEditMode: boolean
  canAddToList: boolean
  interfaceWarningIndices: readonly number[]
  expandedMenu: SelectorScreenMenuType
  setExpandedMenu: (menu: SelectorScreenMenuType) => void
  shouldShowEntryAnimations: boolean
  onChangeSeries: (series: ProductSeries) => void
}

const SwipePagerProductItem: React.FC<{
  product: SkinProductOption
  configuredButtonIds: readonly ConfiguredInterface[]
  Products: ProductDataShape
  expandedMenu: SelectorScreenMenuType
  swipeViewImageSize: number
  swipeItemWidth: number
  interfaceWarningIndices: readonly number[]
  isDarkWallColor: boolean
  setSelectedProduct: (product: SkinProductOption) => void
  onClickCustomise: (() => void) | null
  onClickProduct: () => void
  debugMode: boolean
}> = React.memo(function UnmemoedSwipePagerProductItem({
  product,
  configuredButtonIds,
  Products,
  expandedMenu,
  swipeViewImageSize,
  interfaceWarningIndices,
  onClickProduct,
  // canAddToList,
  debugMode,
}) {
  const scale = 1//product.orientation === 'Horizontal' ? 0.9 : 1

  const isOtherType = expandedMenu === SelectorScreenMenuType.TopLevel && getTopLevelMenuCategoryForProduct(product, SelectorScreenMenuType.TopLevel) === 'Other'
  const getPlaceholderImage = () => {
    if (isOtherType) {
      return product.pro ? OtherProPlaceholderImage : OtherPlaceholderImage
    }
  }

  const scaleMultiplier = Math.min(Math.max(0, (swipeViewImageSize * scale) / 300), 1)

  return (
    <React.Fragment key={`${product.gridProduct?.baseItemNo ?? product.itemNo}`}>
      <SquareContainer
        size={`${swipeViewImageSize * scale}px`}
        insetPercent={5}
        onClick={onClickProduct}
      >
        <SelectorProductItem
          interfaceWarningIndices={interfaceWarningIndices}
          product={product}
          configuredButtonIds={configuredButtonIds}
          Products={Products}
          shadowScale={scaleMultiplier * (isOtherType ? 0.4 : 1)}
          overrideImage={getPlaceholderImage()}
          debugMode={debugMode}
        />
      </SquareContainer>
    </React.Fragment>
  )
})

const ProductSelectionAnalyticsFilterCategory = 'product selection'
const MenuLabelOrientation = 'Orientation'

function getSocketCountDisplayName(socketCount: number) {
  switch (socketCount) {
    case 1: return 'Single'
    case 2: return 'Double'
    case 4: return 'Quad'
    default: return `${socketCount}`
  }
}

export const SelectorScreen: React.FC<Props> = ({
  isEntryFromDeepLink,
  selectedProduct,
  allProducts,
  setSelectedProduct,
  configuredButtonIds,
  onClickCustomise,
  onClickSave,
  isInEditMode,
  canAddToList,
  expandedMenu,
  setExpandedMenu,
  shouldShowEntryAnimations,
  interfaceWarningIndices,
  onChangeSeries,
}) => {
  const wholesalerContext = React.useContext(WholesalerContext)
  const appContext = React.useContext(ConfiguratorAppContext)
  const Products = React.useContext(ProductDataContext)
  const analyticsLogger = React.useContext(AnalyticsContext)

  const selectedProductId = selectedProduct.itemNo
  const selectedStyle = selectedProduct.style

  const selectedGangNumber = selectedProduct.gridProduct?.gangNo ?? 0
  const selectedSlotNumber = getSkinSlotCount(selectedProduct)
  const extraSwitchesSelected = selectedSlotNumber > 0
  const selectedSocketNumber = selectedProduct.gridProduct?.socketNo ?? 0
  const [dragIndexOffset, setDragIndexOffset] = React.useState(0)

  const [size, setSize] = React.useState<[number, number]>([100, 100])

  const [productPopupShown, setProductPopupShown] = React.useState(false)

  const wiserSwitchesProductSelected = selectedProduct.itemNo === WiserSwitchesProductItemNo

  const isProSeriesSelected = !!selectedProduct.pro

  const setIsProSeriesSelected = (isPro: boolean) => {
    onChangeSeries(isPro ? ProductSeries.Pro : ProductSeries.Iconic)
  }

  /**
   * Expands the selected menu, and possibly selects a different initial product when coming from the type menu
   * @param menuToExpand
   */
  const onExpandMenu = (menuToExpand: SelectorScreenMenuType) => {
    setExpandedMenu(menuToExpand)

    //Unique case: Outdoor menu should initially default to the first item in the sorted list
    if (menuToExpand === SelectorScreenMenuType.OutdoorType) {
      const defaultProduct = productsAvailableForUserTypeAndSeries.find(item => item.style === 'Outdoor') ?? null

      if (defaultProduct) {
        setSelectedProduct(defaultProduct)
      }
    //Unique case: 'Wiser Switches' product should expand Style menu with the default Switch product
    } else if (expandedMenu === SelectorScreenMenuType.WiserType && menuToExpand === SelectorScreenMenuType.Style) {
      const defaultProduct = allProducts.find((skinProduct) => DefaultSwitchSelectionProducts.includes(skinProduct.itemNo))

      if (defaultProduct) {
        setSelectedProduct(defaultProduct)
      }
    } else {
      const currentProductTopLevelCategory = getTopLevelMenuCategoryForProduct(selectedProduct, expandedMenu)
      const nextProductTopLevelCategory = getTopLevelMenuCategoryForProduct(selectedProduct, menuToExpand)

      // If changing the menu will result in a different top-level category being selected,
      //    swap to a new product in the current category instead
      // Example: opening "Type" menu from a wiser power point should keep "Wiser" selected, not "Power Points"
      if (currentProductTopLevelCategory !== nextProductTopLevelCategory) {
        const defaultProduct = allProducts.find(item => getTopLevelMenuCategoryForProduct(item, menuToExpand) === currentProductTopLevelCategory) ?? null

        if (defaultProduct) {
          setSelectedProduct(defaultProduct)
        }
      }
    }
  }

  const isElectrician = appContext.userType === UserType.Electrician

  const styleOptions = React.useMemo(() => getStyleMenuOptionsAvailableForUser(!isElectrician, isProSeriesSelected), [isElectrician, isProSeriesSelected])
  const productsAvailableForUserTypeAndSeries = React.useMemo(() => getProductsAvailableForUserAndSeries(allProducts, !isElectrician, isProSeriesSelected), [allProducts, isElectrician, isProSeriesSelected])

  const selectedType = selectedProduct.type
  const selectedTopLevelMenuCategory = getTopLevelMenuCategoryForProduct(selectedProduct, expandedMenu)

  const availableMenus = getAvailableMenusForTopLevelProductType(selectedTopLevelMenuCategory)

  const displayedProducts: readonly SkinProductOption[] = React.useMemo(() => {
    // Unique case: top level selection
    if (expandedMenu === SelectorScreenMenuType.TopLevel) {
      // initially displayed products for each type
      // tries to find specific products by item no., falls back to first item of the type
      const topLevelProductsReducer = (accumulatedProducts: readonly string[], productType: { type: TopLevelMenuCategory, defaultItems: readonly string[] }) => {
        const exactMatch = allProducts.find(it => productType.defaultItems.includes(it.itemNo) && !!it.pro === isProSeriesSelected)

        if (exactMatch) {
          return accumulatedProducts.concat(exactMatch.itemNo)
        }

        // Special case, no Outdoor in Pro
        if (isProSeriesSelected && (productType.type === 'Outdoor' || productType.type === 'Wiser')) {
          return accumulatedProducts
        }

        const fallbackMatch = allProducts.find(it => !!it.pro === isProSeriesSelected && productType.type === getTopLevelMenuCategoryForProduct(it, SelectorScreenMenuType.TopLevel))
          ?? throwErrorInline(`product not found for type "${productType.type}"`)

        return accumulatedProducts.concat(fallbackMatch.itemNo)
      }

      const skinIds = TopLevelSelectionProducts
        .reduce<readonly string[]>(topLevelProductsReducer, [])

      return skinIds.map((partNo) => findSkinByItemNo(partNo, allProducts))
        .sort((a, b) => {
          return TopLevelSortOrderMap[getTopLevelMenuCategoryForProduct(a, SelectorScreenMenuType.TopLevel)] - TopLevelSortOrderMap[getTopLevelMenuCategoryForProduct(b, SelectorScreenMenuType.TopLevel)]
        })

    // Unique case: No additional filtering is applied for Outdoor
    } else if (
      selectedTopLevelMenuCategory === 'Outdoor'
        || expandedMenu === SelectorScreenMenuType.OutdoorType
    ) {
      return productsAvailableForUserTypeAndSeries.filter(it => getTopLevelMenuCategoryForProduct(it, expandedMenu) === 'Outdoor')

    // Unique case: No additional filtering is applied for Wiser
    } else if (
      selectedTopLevelMenuCategory === 'Wiser'
        || expandedMenu === SelectorScreenMenuType.WiserType
    ) {
      return productsAvailableForUserTypeAndSeries
        .filter(it => getTopLevelMenuCategoryForProduct(it, expandedMenu) === 'Wiser' || it.itemNo === WiserSwitchesProductItemNo)

    // Common case: filter based on menu selection
    } else {
      switch (expandedMenu) {
        case SelectorScreenMenuType.Style:
          if (selectedType === 'Other') {
            return filterByBaseItemNoAndColor(
              selectedProduct,
              productsAvailableForUserTypeAndSeries
                .filter(product => doesProductPartialMatch(selectedProduct, product, expandedMenu, ['type']))
            )
          }

          if (selectedType === 'Socket') {
            return filterByBaseItemNoAndColor(
              selectedProduct,
              productsAvailableForUserTypeAndSeries
                .filter(product => doesProductPartialMatch(selectedProduct, product, expandedMenu, ['has-slots', 'type', 'orientation', 'sockets']))
            )
          }

          return filterByBaseItemNoAndColor(
            selectedProduct,
            productsAvailableForUserTypeAndSeries
              .filter(product => doesProductPartialMatch(selectedProduct, product, expandedMenu, ['slots', 'type', 'orientation', 'sockets']))
          )
        case SelectorScreenMenuType.Gang:
          return filterByBaseItemNoAndColor(
            selectedProduct,
            productsAvailableForUserTypeAndSeries
              .filter(product => doesProductPartialMatch(selectedProduct, product, expandedMenu, ['color', 'style', 'orientation', 'type', 'sockets']))
          )

        case SelectorScreenMenuType.Orientation:
          return allProducts.filter(product => {
            // same product in any orientation
            return product.itemNo === selectedProduct.itemNo
              // explicit other orientation product
              || (
                selectedProduct.otherOrientation !== null
                && product.itemNo === selectedProduct.otherOrientation
              )
          })
            .sort((a, b) => b.orientation.localeCompare(a.orientation))

        case SelectorScreenMenuType.Sockets:
          return filterByBaseItemNoAndColor(
            selectedProduct,
            productsAvailableForUserTypeAndSeries
              .filter(product => doesProductPartialMatch(selectedProduct, product, expandedMenu, ['color', 'style', 'orientation', 'type', 'has-slots']))
          )
      }
    }
  }, [selectedTopLevelMenuCategory, productsAvailableForUserTypeAndSeries, expandedMenu, selectedType, selectedProduct, allProducts, isProSeriesSelected])

  const topLevelMenuOptions = React.useMemo(() => {
    return TopLevelMenuOptions.filter(it => displayedProducts.find(product =>
      getTopLevelMenuCategoryForProduct(product, SelectorScreenMenuType.TopLevel) === it.key
    ) !== undefined)
  }, [displayedProducts])

  const topLevelMenuOptionsForUserAndSeries = React.useMemo(() => {
    return TopLevelMenuOptions.filter(it => productsAvailableForUserTypeAndSeries.find(product =>
      getTopLevelMenuCategoryForProduct(product, SelectorScreenMenuType.TopLevel) === it.key
    ) !== undefined)
  }, [productsAvailableForUserTypeAndSeries])

  const outdoorTypeMenuOptions = React.useMemo(() => {
    return OutdoorTypeOptions.filter(it => displayedProducts.find(product =>
      product.type === it.key
    ) !== undefined)
  }, [displayedProducts])

  const onClickTopLevelType = (menuType: TopLevelMenuCategory) => {
    setSelectedProduct(displayedProducts.find( it => menuType === getTopLevelMenuCategoryForProduct(it, SelectorScreenMenuType.TopLevel)) ?? (() => { throw Error() })())
  }

  const selectedOrientation = selectedProduct.orientation
  const selectedProductIndex = React.useMemo(() => {
    switch (expandedMenu) {
      case SelectorScreenMenuType.TopLevel:
        return displayedProducts.findIndex(it => getTopLevelMenuCategoryForProduct(selectedProduct, expandedMenu) === getTopLevelMenuCategoryForProduct(it, expandedMenu))
      default:
        return displayedProducts.findIndex(it => it.itemNo === selectedProductId && it.orientation === selectedOrientation)
    }
  }, [displayedProducts, expandedMenu, selectedOrientation, selectedProductId, selectedProduct])

  const isTopLevelMenuExpanded = expandedMenu === SelectorScreenMenuType.TopLevel

  React.useEffect(() => {
    if (!isTopLevelMenuExpanded) {
      analyticsLogger.log({
        event: 'view_item',
        ecommerce: getEcommercePayloadFrom(
          [{
            product: getAnalyticsProductInfo(selectedProduct),
            quantity: 1,
            price: null,
          }],
          {
            name: appContext.listName,
            id: appContext.listId
          }
        )
      })
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedProduct, isTopLevelMenuExpanded])

  const trySetSelectedOrientation = React.useCallback((newOrientation: ProductOrientationSupport): boolean => {
    // Callback returns true if the orientation is successfully selected,
    //   false if no product is found
    const newProduct = displayedProducts.find(product => product.orientation === newOrientation)

    if (newProduct) {
      setSelectedProduct(newProduct)
      return true
    } else {
      console.warn('SelectorScreen.setSelectedOrientation orientation not found!', selectedProduct, newOrientation)
      return false
    }
  }, [displayedProducts, selectedProduct, setSelectedProduct])

  const setSelectedGangNumber = React.useCallback((gangNumber: number) => {
    const newProduct = displayedProducts.find(product =>
      doesProductPartialMatch(selectedProduct, product, expandedMenu, ['color', 'style', 'orientation', 'type'])
        && product.gridProduct?.gangNo === gangNumber
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, selectedProduct, setSelectedProduct, expandedMenu])

  const setHasExtraSwitches = React.useCallback((hasExtraSwitches: boolean) => {
    const newProduct = displayedProducts.find(product =>
      doesProductPartialMatch(selectedProduct, product, expandedMenu, ['color', 'style', 'orientation', 'type'])
        && (
          hasExtraSwitches
            ? doesSkinHaveSlots(product)
            : !doesSkinHaveSlots(product)
        )
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, selectedProduct, setSelectedProduct, expandedMenu])

  const setSelectedSocketNumber = React.useCallback((socketCount: number) => {
    const newProduct = displayedProducts.find(product =>
      product?.gridProduct?.socketNo === socketCount
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, setSelectedProduct])

  const setSelectedRange = React.useCallback((style: IconicRange) => {
    const newProduct = displayedProducts.find(product =>
      product.style === style
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, setSelectedProduct])

  const setSelectedType = React.useCallback((productType: BaseProductType) => {
    const newProduct = displayedProducts.find(product =>
      product.type === productType
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, setSelectedProduct])

  const setSelectedSubcategory = React.useCallback((subcategory: string) => {
    const newProduct = displayedProducts.find(product =>
      product.displaySubcategory === subcategory
    )

    if (newProduct) {
      setSelectedProduct(newProduct)
    }
  }, [displayedProducts, setSelectedProduct])

  const setSelectedProductIndex = React.useCallback((unroundedIndex: number) => {
    const index = Math.round(unroundedIndex)
    const productAtIndex = displayedProducts[index]

    if (productAtIndex) {
      setSelectedProduct(productAtIndex)

      const remainder = unroundedIndex - index
      setDragIndexOffset(remainder)
    }
  }, [displayedProducts, setSelectedProduct])

  const swipePageLabelInnerHeight = expandedMenu === SelectorScreenMenuType.TopLevel ? 60 : 160
  const swipePagePaddingHeight = 20
  const swipePageTopPaddingHeight = 20
  const swipePageLabelOuterHeight = swipePageLabelInnerHeight + swipePagePaddingHeight + swipePageTopPaddingHeight

  const wallColor = appContext.wallColor

  const isDarkWallColor = !isColorLight(wallColor)
  const isLowContrastWallColor = isColorLowContrast(wallColor)

  const swipeItemWidth = Math.max(
    Math.min(
      size[1] - swipePageLabelOuterHeight, size[0] * 0.6,
      300
    ),
    // 200
  )

  const swipeItemHeight = Math.min(size[1], Math.min(size[1] - swipePageLabelOuterHeight, size[0] * 0.6, 300))
  // const swipeItemHeight = Math.min(size[1], Math.min(size[1] - swipePageLabelOuterHeight, size[0] * 0.6, 300)) + 20
  // const swipeItemHeight = swipeItemWidth + 20
  const swipeViewImageSize = Math.min(swipeItemHeight, swipeItemWidth)

  const menuScrollContainerRef = React.useRef<HTMLDivElement | null>(null)

  const animationDuration = 3000

  const initialMenuAnimationState = useOneshotDuration(shouldShowEntryAnimations ? animationDuration : 0, shouldShowEntryAnimations ? 300 : 0)

  const [isMenuOverflowingInitially, setIsMenuOverflowingInitially] = React.useState(false)

  const menuAnimationState = isMenuOverflowingInitially ? initialMenuAnimationState : 'complete'

  React.useLayoutEffect(() => {
    const menuScrollContainer = menuScrollContainerRef.current
    if (menuScrollContainer && shouldShowEntryAnimations) {
      menuScrollContainer.scrollLeft = 0
      // overflow, show entry animation
      if (menuScrollContainer.clientWidth < menuScrollContainer.scrollWidth) {
        setIsMenuOverflowingInitially(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // const [shouldShowMenuScrollIndicator, setShouldShowMenuScrollIndicator] = React.useState(true)

  // const canDismissMenuScrollIndicator = useDelay(500)

  const otherOptions = getAdditionalSkinGridOptions(selectedProduct, Products.skinGridProducts)

  const [[menuOverflowingLeft, menuOverflowingRight], setMenuOverflowing] = React.useState<[boolean, boolean]>([false, false])

  const updateMenuOverflowState = React.useCallback(() => {
    const el = menuScrollContainerRef.current

    if (!el) {
      return null
    }

    setMenuOverflowing(() => {
      const outerWidth = el.clientWidth
      const innerWidth = el.scrollWidth

      const minScrollDistance = 20

      if (innerWidth > outerWidth) {
        const canScrollLeft = el.scrollLeft > minScrollDistance
        const canScrollRight = el.scrollLeft < (innerWidth - outerWidth) - minScrollDistance

        return [canScrollLeft, canScrollRight]
      }

      return [false, false]
    })
  }, [])

  const smoothScroll = useSmoothScroller()

  const onClickScrollMenu = (direction: 'left' | 'right') => {
    if (menuScrollContainerRef.current && menuAnimationState === 'complete') {
      const element = menuScrollContainerRef.current

      // try to find the next menu in the specified direction and scroll to it
      const menus = Array.from(element.querySelectorAll(`.${ExpandingOptionSelectorClassName}`) as Iterable<HTMLDivElement>)
      const offset = (parseInt(getComputedStyle(element).marginLeft) || 40) + 10

      const currentScroll = element.scrollLeft

      const nextElement = direction === 'left'
        ? menus.reverse().find(it => it.offsetLeft < (currentScroll + offset))
        : menus.find(it => it.offsetLeft > (currentScroll + offset))

      if (nextElement) {
        const finalScrollOffset = nextElement.offsetLeft
        smoothScroll(element, currentScroll, finalScrollOffset - offset)
      } else {
        smoothScroll(element, currentScroll, element.scrollWidth)
      }
    }
  }

  const optimalGridProduct = React.useMemo(() => {
    const standaloneGridItemNo = selectedProduct.gridProduct?.standaloneGridItemNo

    if (standaloneGridItemNo) {
      const gridOptions = getAllGridOptions(standaloneGridItemNo, Products.grids)
      return findOptimalGrid(selectedProduct, selectedProduct.orientation, getDefaultButtons(selectedProduct, Products).map(it => it.mechItemNo), gridOptions)
    }

    return selectedProduct.gridProduct
  }, [Products, selectedProduct])

  const doesOptimalGridIncludeSkin = optimalGridProduct?.includedSkinItemNo === selectedProduct.itemNo

  const displaySkinItemNo = doesOptimalGridIncludeSkin ? optimalGridProduct?.itemNo : selectedProduct.itemNo
  const displayGridItemNo = doesOptimalGridIncludeSkin || optimalGridProduct === null ? null : optimalGridProduct.itemNo

  // Set primary heading and secondary heading depending on whether item has a style/colour.
  const { primary: primaryItemHeading, secondary: secondaryItemHeading }
    = getProductDisplayNames(selectedProduct)

  React.useEffect(() => {
    if (wholesalerContext) {
      const productsToFetch = [displaySkinItemNo, displayGridItemNo].filter((it): it is string => typeof it === 'string')

      if (productsToFetch.length > 0) {
        wholesalerContext.fetchWholesalerPrice(productsToFetch)
      }
    }
  }, [displaySkinItemNo, displayGridItemNo, wholesalerContext])

  const displayTypeSelectorHint = !appContext.typeSelectorTutorialDismissed && isEntryFromDeepLink

  // Use non-breaking space (\xa0) after "or" so it won't linebreak just before the last item.
  const hintDialogTopLevelTypesList = formatList(topLevelMenuOptionsForUserAndSeries.map<string>(it => it.label), ', ', ' or\xa0')

  const displaySubcategoryMenuOptions = React.useMemo(() => {
    return Array.from(
      displayedProducts.reduce<Set<string>>((accum, it) => {
        if (it.displaySubcategory) {
          accum.add(it.displaySubcategory)
        }

        return accum
      }, new Set<string>()).values()
    )
      .map(it => {
        return { key: it, label: it }
      })
  }, [displayedProducts])

  return (
    <div
      className={
        classNames(
          'SelectorScreen',
          { defaultWallColor: wallColor === WallColors[0].color }
        )
      }
      style={{
        backgroundColor: wallColor,
        transition: 'background-color 500ms',
      }}
    >
      {
        isElectrician && ENABLE_ELECTRICIAN_VIEW && appContext.proSeriesEnabled && (
          <div className="seriesSelectorBar">
            <ToggleSeries
              proSelected={isProSeriesSelected}
              onClick={() => {
                setIsProSeriesSelected(!isProSeriesSelected)

                analyticsLogger.log({
                  event: 'filter_clicked',
                  filter_name: isProSeriesSelected ? 'Iconic' : 'Pro Series',
                  filter_action: 'filter selected',
                  filter_category: 'product series',
                  filter_sub_category: null,
                })
              }}
            />
          </div>
        )
      }
      <div className="contentMain">
        <div className="swipeViewWrapper">
          {
            !canAddToList && (
              <ValidationBanner
              // bottom={
                // offset to match top of details area; swipePageLabelOuterHeight + half * padding around pager area
                // `${swipePageLabelOuterHeight + (size[1] - swipePageLabelOuterHeight - swipeItemHeight)/2 - 20}px`
              // }
                top={'10px'}
              >
                This combination isn&apos;t allowed<br/>{windowSupportsTouchEvents ? 'Tap' : 'Click'} Customise Switches to change it
              </ValidationBanner>
            )
          }
          <ReactResizeDetector handleHeight handleWidth onResize={(x, y) => setSize([x, y])} />
          <SwipeView
            key={`swipeview-${expandedMenu}-${isProSeriesSelected ? 'pro' : 'iconic'}`}
            itemHeight={swipeItemHeight}
            itemWidth={swipeItemWidth}
            onSelected={setSelectedProductIndex}
            selectedIndex={selectedProductIndex + dragIndexOffset}
            setSelectedIndex={setSelectedProductIndex}
            onClickSelected={() => { return }}
          >
            {
              displayedProducts.map((product, i) => (
                <SwipePagerProductItem
                  debugMode={appContext.debugMode}
                  onClickProduct={() => {
                    if (i === selectedProductIndex) {
                      if (expandedMenu === SelectorScreenMenuType.TopLevel) {
                        onExpandMenu(getDefaultMenuForTopLevelType(selectedTopLevelMenuCategory))
                      } else if (wiserSwitchesProductSelected) {
                        appContext.dispatch({
                          action: AppStateActions.WiserModeChanged,
                          newMode: { type: WiserOrMomentary.Wiser, mechOverride: '' }
                        })

                        onExpandMenu(getDefaultMenuForTopLevelType('Switch'))
                      } else {
                        setProductPopupShown(true)
                      }
                    }
                  }}
                  key={i}
                // isSelected={Math.round(selectedProductIndex) === i}
                  interfaceWarningIndices={Math.round(selectedProductIndex) === i ? interfaceWarningIndices : EmptyArray}
                  {...{
                    product,
                    configuredButtonIds,
                    Products,
                    expandedMenu,
                    swipeViewImageSize,
                    swipeItemWidth,
                  // swipePagePaddingHeight,
                  // swipePageLabelInnerHeight,
                  // selectedType,
                    isDarkWallColor,
                    setSelectedProduct,
                    onClickCustomise,
                    onClickSave,
                  // isInEditMode,
                    canAddToList,
                  }}
                />
              ))
            }
          </SwipeView>
          {
            selectedProduct && selectedProductIndex > -1 && (
              <div
                key={`${selectedProductIndex} ${expandedMenu}`}
                style={{
                  transition: '200ms',
                // transform: isSelected ? 'translate(0, 0)' : 'translate(0, 100%)',
                // opacity: isSelected ? 1 : 0,
                  textAlign: 'center',
                // margin: `${swipePagePaddingHeight}px 0 0 0`,
                  height: `${swipePageLabelInnerHeight}px`,
                  animation: 'detailsUp 200ms ease',
                }}
              >
                <div className={classNames('selectedItemHeading', { lightText: isDarkWallColor, lowContrast: isLowContrastWallColor })}>
                  {
                    (expandedMenu === SelectorScreenMenuType.TopLevel && (
                      <h3>{getTopLevelCategoryDisplayName(selectedTopLevelMenuCategory)}</h3>
                    )) || (
                      <>
                        <h3>
                          {primaryItemHeading}
                        </h3>
                        <span className="details">
                          {secondaryItemHeading}
                          {/* Skin and Grid item number(s) for electrician view */}
                          { isElectrician && !wiserSwitchesProductSelected && (
                            <span className="itemNo">
                              <br />
                              {[displaySkinItemNo, displayGridItemNo].filter(it => it !== null).join(' + ')}
                            </span>
                          )}
                          {/*{displaySkinItemNo && (*/}
                          {/*  <StockPricingDisplay itemNo={displaySkinItemNo} label={displayGridItemNo ? 'Skin' : 'Composite'} />*/}
                          {/*)}*/}
                          {/*{displayGridItemNo && (*/}
                          {/*  <StockPricingDisplay itemNo={displayGridItemNo} label={displayGridItemNo ? 'Grid' : 'Composite'}/>*/}
                          {/*)}*/}
                        </span>
                        {
                          otherOptions.length > 1 && (
                            <div className="ampSelector">
                              {
                                otherOptions.map((it, i) => (
                                  <Button
                                    key={i}
                                    buttonStyle={it.itemNo === selectedProduct.itemNo ? 'green' : undefined}
                                    onClick={() => {
                                      analyticsLogger.log({
                                        event: 'filter_clicked',
                                        filter_name: it.gridProduct.comparisonName ?? it.gridProduct.displayName,
                                        filter_action: 'filter selected',
                                        filter_category: 'product variant',
                                        filter_sub_category: null,
                                      })

                                      setSelectedProduct(it)
                                    }}
                                  >
                                    {it.gridProduct.comparisonName}
                                  </Button>
                                ))
                              }
                            </div>
                          )
                        }
                      </>
                    )
                  }
                </div>
                <div className="buttonsColumn">
                  <div className="buttonsColumnInner">
                    {
                      expandedMenu !== SelectorScreenMenuType.TopLevel
                      // && isSelected
                      && ((selectedProduct.gridProduct
                          && doesSkinHaveSlots(selectedProduct)
                          && onClickCustomise
                      ) || wiserSwitchesProductSelected)
                    && (
                      <Button
                        onClick={() => {
                          analyticsLogger.log({
                            event: 'cta_click',
                            cta_name: BuildConfig.brand.copy.SelectorScreen.ButtonCustomiseSwitches,
                            cta_link: null,
                            cta_category: selectedProduct.itemNo,
                            cta_location: 'Product selector'
                          })

                          if (onClickCustomise) { onClickCustomise() }
                        }}
                      >
                        {BuildConfig.brand.copy.SelectorScreen.ButtonCustomiseSwitches}
                      </Button>
                    )
                    }

                    <div style={{ height: '10px' }} />

                    {
                      expandedMenu !== SelectorScreenMenuType.TopLevel
                      && !isInEditMode
                      && !wiserSwitchesProductSelected
                    && (
                      <Button
                        buttonStyle="green"
                        onClick={() => {
                          analyticsLogger.log({
                            event: 'cta_click',
                            cta_name: BuildConfig.brand.copy.ButtonAddToMyList,
                            cta_link: null,
                            cta_category: 'Button',
                            cta_location: 'Product selector'
                          })

                          if (onClickSave) { onClickSave() }
                        }}
                        disabled={!canAddToList}
                      >
                        <img src={IconPlusWhite} alt="+" style={{ marginRight: '8px' }} />
                        {BuildConfig.brand.copy.ButtonAddToMyList}
                      </Button>
                    )
                    }
                  </div>
                </div>
              </div>
            )
          }
          {/*<div className={classNames('menuScrollIndicator', { shown: shouldShowMenuScrollIndicator && isMenuOverflowingInitially })}>*/}
          {/*  <span className="scrollText">Scroll</span>*/}
          {/*  <img src={IconScrollArrow} alt="" />*/}
          {/*</div>*/}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <PrivacyPolicyLink />
          <CookieSettingsLink />
        </div>
      </div>


      <div className="footer">
        <div
          className={classNames('menuContainerOuter', { animating: menuAnimationState !== 'complete', hintShown: displayTypeSelectorHint })}
          ref={menuScrollContainerRef}
          onScroll={e => {
            // if (canDismissMenuScrollIndicator) {
            //   setShouldShowMenuScrollIndicator(false)
            // }

            updateMenuOverflowState()
          }}
        >
          <ReactResizeDetector
            handleWidth
            onResize={size => updateMenuOverflowState()}
          />
          <div
            className={classNames('menuContainer', { preAnimation: menuAnimationState === 'start' })}
            style={{
              transition: menuAnimationState !== 'start' ? `${animationDuration}ms` : undefined,
            }}
          >
            <HintWrapper
              isShown={displayTypeSelectorHint}
              responsive
              appearDelayMs={2500}
              hintPosition={HintPosition.AboveRight}
              message={`Click here to choose ${hintDialogTopLevelTypesList}`}
              handleDismiss={() => { appContext.dispatch({ action: AppStateActions.TypeSelectorTutorialDismissed }) }}
            >
              <ExpandingOptionSelector
                key="toplevelselector"
                selected={getTopLevelMenuCategoryForProduct(selectedProduct, expandedMenu)}
                onExpand={() => {
                  analyticsLogger.log({
                    event: 'filter_clicked',
                    filter_action: 'filter selected',
                    filter_category: ProductSelectionAnalyticsFilterCategory,
                    filter_sub_category: null,
                    filter_name: BuildConfig.brand.copy.SelectorScreen.MenuType,
                  })

                  onExpandMenu(SelectorScreenMenuType.TopLevel)
                }}
                expanded={expandedMenu === SelectorScreenMenuType.TopLevel}
                setSelected={item => {
                  analyticsLogger.log({
                    event: 'filter_clicked',
                    filter_action: 'filter selected',
                    filter_category: ProductSelectionAnalyticsFilterCategory,
                    filter_sub_category: BuildConfig.brand.copy.SelectorScreen.MenuType,
                    filter_name: TopLevelMenuOptions.find(option => option.key === item)?.label ?? item,
                  })

                  onClickTopLevelType(item)
                }}
                label={BuildConfig.brand.copy.SelectorScreen.MenuType}
                options={topLevelMenuOptions}
                icon={IconType}
              />
            </HintWrapper>

            {availableMenus.has(SelectorScreenMenuType.Style) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector
                  selected={selectedStyle}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: BuildConfig.brand.copy.SelectorScreen.MenuStyle,
                    })

                    onExpandMenu(SelectorScreenMenuType.Style)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.Style}
                  setSelected={(range: IconicRange) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: BuildConfig.brand.copy.SelectorScreen.MenuStyle,
                      filter_name: range,
                    })

                    setSelectedRange(range)
                  }}
                  label={BuildConfig.brand.copy.SelectorScreen.MenuStyle}
                  options={styleOptions}
                  icon={IconStyle}
                />
              </>
            )}

            {availableMenus.has(SelectorScreenMenuType.OutdoorType) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector<BaseProductType>
                  selected={selectedType}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: 'Outdoor type',
                    })

                    onExpandMenu(SelectorScreenMenuType.OutdoorType)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.OutdoorType}
                  setSelected={(type: BaseProductType) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: 'Outdoor type',
                      filter_name: type,
                    })

                    setSelectedType(type)
                  }}
                  label={'Type'}
                  options={outdoorTypeMenuOptions}
                  icon={IconType}
                />
              </>
            )}

            {availableMenus.has(SelectorScreenMenuType.WiserType) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector<string>
                  selected={selectedProduct.displaySubcategory ?? null}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: 'Wiser type',
                    })

                    onExpandMenu(SelectorScreenMenuType.WiserType)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.WiserType}
                  setSelected={(type: string) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: 'Wiser type',
                      filter_name: type,
                    })

                    setSelectedSubcategory(type)
                  }}
                  label={'Type'}
                  options={displaySubcategoryMenuOptions}
                  icon={IconType}
                />
              </>
            )}

            {availableMenus.has(SelectorScreenMenuType.Gang) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector
                  selected={selectedGangNumber}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: BuildConfig.brand.copy.SelectorScreen.MenuButtons,
                    })

                    onExpandMenu(SelectorScreenMenuType.Gang)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.Gang}
                  setSelected={(option) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: BuildConfig.brand.copy.SelectorScreen.MenuButtons,
                      filter_name: `${option}`,
                    })

                    setSelectedGangNumber(option)
                  }}
                  label={BuildConfig.brand.copy.SelectorScreen.MenuButtons}
                  options={
                    Object.keys(
                      displayedProducts.reduce<{ [key: number]: any }>((accum, p) => {
                        accum[p.gridProduct?.gangNo ?? 0] = true
                        return accum
                      }, {})
                    )
                      .map(it => ({ key: parseInt(it), label: it }))
                  }
                  icon={IconButtons}
                />
              </>
            )}

            {availableMenus.has(SelectorScreenMenuType.Sockets) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector
                  selected={selectedSocketNumber}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: BuildConfig.brand.copy.SelectorScreen.MenuSockets,
                    })

                    onExpandMenu(SelectorScreenMenuType.Sockets)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.Sockets}
                  setSelected={(option) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: BuildConfig.brand.copy.SelectorScreen.MenuSockets,
                      filter_name: getSocketCountDisplayName(option),
                    })

                    setSelectedSocketNumber(option)
                  }}
                  label={BuildConfig.brand.copy.SelectorScreen.MenuSockets}
                  options={
                    lodash.uniq(
                      displayedProducts.map(item => item.gridProduct?.socketNo ?? 0)
                    )
                      .map(socketCount => {
                        const label: string = getSocketCountDisplayName(socketCount)
                        return { key: socketCount, label }
                      })
                  }
                  icon={IconMenuSocket}
                />

                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector
                  selected={extraSwitchesSelected ? 'Yes' : 'No'}
                  selectedCollapsedLabel={extraSwitchesSelected ? 'Yes' : 'No'}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: BuildConfig.brand.copy.SelectorScreen.MenuExtraSwitches,
                    })

                    onExpandMenu(SelectorScreenMenuType.Gang)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.Gang}
                  setSelected={(option) => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: BuildConfig.brand.copy.SelectorScreen.MenuExtraSwitches,
                      filter_name: option,
                    })

                    setHasExtraSwitches(option === 'Yes')
                  }}
                  label={BuildConfig.brand.copy.SelectorScreen.MenuExtraSwitches}
                  options={[
                    {
                      key: 'No',
                      label: 'No',
                    }, {
                      key: 'Yes',
                      label: 'Yes',
                    }]
                  }
                  icon={IconButtons}
                />

              </>
            )}

            {availableMenus.has(SelectorScreenMenuType.Orientation) && (
              <>
                <div style={{ width: '20px' }} />

                <ExpandingOptionSelector
                  selected={selectedOrientation}
                  onExpand={() => {
                    analyticsLogger.log({
                      event: 'filter_clicked',
                      filter_action: 'filter selected',
                      filter_category: ProductSelectionAnalyticsFilterCategory,
                      filter_sub_category: null,
                      filter_name: MenuLabelOrientation,
                    })

                    onExpandMenu(SelectorScreenMenuType.Orientation)
                  }}
                  expanded={expandedMenu === SelectorScreenMenuType.Orientation}
                  setSelected={item => {
                    // Both orientations are always shown,
                    //   but selection may fail if no products match
                    if (trySetSelectedOrientation(item)) {
                      analyticsLogger.log({
                        event: 'filter_clicked',
                        filter_action: 'filter selected',
                        filter_category: ProductSelectionAnalyticsFilterCategory,
                        filter_sub_category: MenuLabelOrientation,
                        filter_name: item,
                      })
                    }
                  }}
                  label={MenuLabelOrientation}
                  options={OrientationOptions}
                  icon={IconOrientation}
                />
              </>
            )}
          </div>
        </div>

        {(
          <div
            className={classNames('scrollArrow left', { gone: !menuOverflowingLeft || menuAnimationState !== 'complete' })}
            onClick={() => onClickScrollMenu('left')}
          >
            <img src={IconScrollLeft} alt="left" />
          </div>
        )}

        {(
          <div
            className={classNames('scrollArrow right', { gone: !menuOverflowingRight || menuAnimationState !== 'complete' })}
            onClick={() => onClickScrollMenu('right')}
          >
            <img
              src={IconScrollLeft}
              alt="right"
              style={{
                transform: 'scaleX(-1)',
              }}
            />
          </div>
        )}

      </div>
      {

        productPopupShown && (
          <SelectorProductPopup
            interfaceWarningIndices={interfaceWarningIndices}
            product={selectedProduct}
            configuredButtonIds={configuredButtonIds}
            Products={Products}
            onClose={() => setProductPopupShown(false)}
            onClickAddToList={
              canAddToList
                ? () => {
                  analyticsLogger.log({
                    event: 'cta_click',
                    cta_name: 'Add to my list',
                    cta_link: null,
                    cta_category: 'Button',
                    cta_location: 'Product selector'
                  })

                  setProductPopupShown(false)
                  onClickSave()
                }
                : null
            }
            onClickCustomise={onClickCustomise ? () => {
              analyticsLogger.log({
                event: 'cta_click',
                cta_name: BuildConfig.brand.copy.SelectorScreen.ButtonCustomiseSwitches,
                cta_link: null,
                cta_category: selectedProduct.itemNo,
                cta_location: 'Product selector'
              })

              setProductPopupShown(false)
              onClickCustomise()
            } : null}
          />
        )
      }
    </div>
  )
}
